import { PromptCategoryId, PromptDTO, PromptOwnerType } from "@shaarp/common";
import {
  AlertCircle,
  Calendar,
  CheckCircle2,
  CheckSquare,
  ClipboardCheck,
  Clock,
  Compass,
  Crosshair,
  DollarSign,
  FileSearch,
  FileText,
  Filter,
  Flag,
  Flame,
  Focus,
  Globe,
  Handshake,
  Lightbulb,
  ListChecks,
  ListFilter,
  LucideIcon,
  Mail,
  MessageCircle,
  MessageSquare,
  Network,
  PenLine,
  Phone,
  PhoneCall,
  Radar,
  Route,
  Search,
  Send,
  Shield,
  Sparkles,
  Target,
  User,
  UserCog,
  UserPlus,
  Users,
  Voicemail,
} from "lucide-react";

/**
 * Map of category IDs to their default icons
 */
const CATEGORY_ICON_MAP: Record<PromptCategoryId, LucideIcon> = {
  targeting: Crosshair,
  brief: FileText,
  signals: Radar,
  approach: Route,
  writing: PenLine,
  qualification: ClipboardCheck,
  other: Sparkles,
};

/**
 * Map of owner types to their icons
 */
const OWNER_ICON_MAP: Record<PromptOwnerType, LucideIcon> = {
  user: User,
  team: Users,
  public: Globe,
};

/**
 * Default icon to use when no match is found
 */
const DEFAULT_ICON: LucideIcon = Sparkles;

/**
 * Explicit mapping of icon names to Lucide components
 * Required because dynamic imports don't work with tree-shaking
 */
const ICON_NAME_MAP: Record<string, LucideIcon> = {
  // Communication & Messaging
  Mail,
  MessageCircle,
  MessageSquare,
  Send,
  Phone,
  PhoneCall,
  Voicemail,

  // People & Users
  User,
  UserCog,
  UserPlus,
  Users,
  Handshake,

  // Business & Sales
  Target,
  Crosshair,
  Focus,
  DollarSign,

  // Documents & Files
  FileText,
  FileSearch,
  ClipboardCheck,

  // Writing & Editing
  PenLine,

  // Search & Discovery
  Search,
  Radar,

  // Planning & Organization
  Calendar,
  Clock,
  Flag,
  Filter,
  ListFilter,
  ListChecks,

  // Progress & Status
  CheckCircle2,
  CheckSquare,
  AlertCircle,

  // Ideas & Innovation
  Lightbulb,
  Sparkles,
  Flame,

  // Navigation & Routes
  Route,
  Compass,

  // Network & Connections
  Network,
  Shield,
  Globe,
};

/**
 * Get a Lucide icon component by its name
 * @param iconName - The name of the icon (e.g., "Target", "Mail")
 * @returns The Lucide icon component or the default icon
 */
export function getIconByName(iconName: string | undefined): LucideIcon {
  if (!iconName) {
    return DEFAULT_ICON;
  }

  const icon = ICON_NAME_MAP[iconName];
  if (icon) {
    return icon;
  }

  console.warn(`⚠️ Icon "${iconName}" not found in ICON_NAME_MAP, using default`);
  return DEFAULT_ICON;
}

/**
 * Get the icon for a prompt (works with both DB prompts and legacy prompts)
 * @param prompt - The prompt DTO
 * @returns The Lucide icon component
 */
export function getIconForPrompt(prompt: PromptDTO): LucideIcon {
  // If the prompt has a specific icon set, use it
  if (prompt.icon) {
    return getIconByName(prompt.icon);
  }

  // Fallback to category icon if available
  if (prompt.categoryId) {
    return CATEGORY_ICON_MAP[prompt.categoryId] || DEFAULT_ICON;
  }

  // Fallback to owner type icon
  return OWNER_ICON_MAP[prompt.ownerType] || DEFAULT_ICON;
}

/**
 * Get the icon for a prompt category
 * @param categoryId - The category ID
 * @returns The Lucide icon component
 */
export function getIconForCategory(categoryId: PromptCategoryId): LucideIcon {
  return CATEGORY_ICON_MAP[categoryId] || DEFAULT_ICON;
}

/**
 * Get the icon for a prompt owner type
 * @param ownerType - The owner type
 * @returns The Lucide icon component
 */
export function getIconForOwnerType(ownerType: PromptOwnerType): LucideIcon {
  return OWNER_ICON_MAP[ownerType] || Globe;
}
